// Journal transactions of a customer / supplier / account (legacy TransactionsComponent):
//   GET {res}/transactions/{id}        → { transactions: { start_balance, close_balance }, start_date, end_date }
//   GET {res}/transactions/{id}/table  → { data, count } (debit/credit in cents)
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import { ArrowLeft } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage, fetchSelectOptions, fetchTable, SelectOption } from '../../api/client';
import { LoadingBlock } from '../../components/StateBlock';
import { SectionHeader } from '../../dashboard/components/SectionHeader';
import { formatDateAuto, t } from '../../i18n';
import { centsToMajor, formatAmount } from './helpers';
import { UI } from '../../brand/ui';

export type TransactionsKind = 'customers' | 'suppliers' | 'accounts';

type JournalRow = {
  id: string;
  debit?: number | string | null;
  credit?: number | string | null;
  type?: string;
  created_at?: string;
  subject_id?: string;
  subject_type?: string;
  subject?: Record<string, unknown> | null;
  journal?: { morphed?: Record<string, unknown> | null } | null;
};

type Filters = { start_date: string; end_date: string; user_id: string; customer_id: string; supplier_id: string };
const emptyFilters: Filters = { start_date: '', end_date: '', user_id: '', customer_id: '', supplier_id: '' };

// Keep the server's wall-clock time (the API answers in UTC ISO or in
// "YYYY-MM-DD HH:mm:ss" server time) so re-applying the filter is lossless.
function toInput(value: unknown) {
  if (!value) return '';
  const text = String(value);
  const match = text.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (match) return `${match[1]}T${match[2]}`;
  const parsed = dayjs(text);
  return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : '';
}

function toApi(value: string) {
  return value ? `${value.replace('T', ' ')}:00` : '';
}

function typeChips(type: string | undefined) {
  const [first = '', second = ''] = String(type ?? '').split('_');
  const secondColor = second === 'created' ? 'success' : second === 'updated' ? 'warning' : second === 'deleted' ? 'error' : 'default';
  return (
    <Stack direction="row" spacing={0.5} justifyContent="center">
      {first ? <Chip size="small" color={first === 'opening' ? 'success' : 'primary'} label={first} /> : null}
      {second ? <Chip size="small" color={secondColor} label={second} variant={secondColor === 'default' ? 'outlined' : 'filled'} /> : null}
    </Stack>
  );
}

function subjectLink(row: JournalRow) {
  const type = row.subject_type ?? '';
  if (!row.subject_id) return null;
  if (type.endsWith('\\Payment')) return `/payments?view=${row.subject_id}`;
  if (type.endsWith('\\Income')) return `/incomes?view=${row.subject_id}`;
  if (type.endsWith('\\Expense')) return `/expenses?view=${row.subject_id}`;
  return null;
}

const titles: Record<TransactionsKind, { back: string; list: string }> = {
  customers: { back: '/customers', list: 'parties.backToCustomers' },
  suppliers: { back: '/suppliers', list: 'parties.backToSuppliers' },
  accounts: { back: '/accounts', list: 'parties.backToAccounts' }
};

export function TransactionsPage({ kind }: { kind: TransactionsKind }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [party, setParty] = useState<Record<string, unknown> | null>(null);
  const [draft, setDraft] = useState<Filters>(emptyFilters);
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [summary, setSummary] = useState<{ start: number | null; close: number | null; startDate?: string; endDate?: string } | null>(null);
  const [rows, setRows] = useState<JournalRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'created_at', dir: 'desc' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<SelectOption[]>([]);
  const [customers, setCustomers] = useState<SelectOption[]>([]);
  const [suppliers, setSuppliers] = useState<SelectOption[]>([]);

  const params = useMemo(() => {
    const next: Record<string, string> = {};
    if (filters.start_date) next.start_date = toApi(filters.start_date);
    if (filters.end_date) next.end_date = toApi(filters.end_date);
    if (filters.user_id) next.user_id = filters.user_id;
    if (kind === 'accounts' && filters.customer_id) next.customer_id = filters.customer_id;
    if (kind === 'accounts' && filters.supplier_id) next.supplier_id = filters.supplier_id;
    return next;
  }, [filters, kind]);

  useEffect(() => {
    adminApi
      .get(`${kind}/${id}`)
      .then((response) => setParty(response.data ?? null))
      .catch(() => setParty(null));
    fetchSelectOptions('users/search').then(setUsers).catch(() => setUsers([]));
    if (kind === 'accounts') {
      fetchSelectOptions('customers/search').then(setCustomers).catch(() => setCustomers([]));
      fetchSelectOptions('suppliers/search').then(setSuppliers).catch(() => setSuppliers([]));
    }
  }, [id, kind]);

  useEffect(() => {
    adminApi
      .get(`${kind}/transactions/${id}`, { params })
      .then((response) => {
        const data = response.data ?? {};
        setSummary({
          start: centsToMajor(data.transactions?.start_balance),
          close: centsToMajor(data.transactions?.close_balance),
          startDate: data.start_date,
          endDate: data.end_date
        });
        setDraft((current) => ({
          ...current,
          start_date: current.start_date || toInput(data.start_date),
          end_date: current.end_date || toInput(data.end_date)
        }));
      })
      .catch((err) => setError(apiErrorMessage(err, t('common.unableToLoad', { name: t('parties.transactions') }))));
  }, [id, kind, params]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchTable<JournalRow>(`${kind}/transactions/${id}/table`, {
        page: page + 1,
        limit,
        orderBy: `${sort.key} ${sort.dir}`,
        extra: params
      });
      setRows(Array.isArray(response.data) ? response.data : []);
      setCount(response.count ?? 0);
    } catch (err) {
      setError(apiErrorMessage(err, t('common.unableToLoad', { name: t('parties.transactions') })));
    } finally {
      setLoading(false);
    }
  }, [id, kind, limit, page, params, sort]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const totals = useMemo(
    () => rows.reduce((sum, row) => ({ debit: sum.debit + (centsToMajor(row.debit) ?? 0), credit: sum.credit + (centsToMajor(row.credit) ?? 0) }), { debit: 0, credit: 0 }),
    [rows]
  );

  const partyName = party ? `${String(party.name ?? '')}${party.company ? ` (${String(party.company)})` : ''}` : '';
  const period = summary?.startDate ? `${formatDateAuto(String(summary.startDate))} – ${formatDateAuto(String(summary.endDate ?? ''))}` : '';
  const sortLabel = (key: string, label: string) => (
    <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : 'asc'} onClick={() => setSort((current) => ({ key, dir: current.key === key && current.dir === 'asc' ? 'desc' : 'asc' }))}>
      {label}
    </TableSortLabel>
  );

  return (
    <Stack spacing={2.5}>
      <SectionHeader title={`${t('parties.transactions')}${partyName ? ` — ${partyName}` : ''}`} description={period || t('parties.transactionsDescription')} />
      <Box>
        <Button size="small" startIcon={<ArrowLeft size={16} />} onClick={() => navigate(titles[kind].back)}>
          {t(titles[kind].list)}
        </Button>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
            <TextField size="small" type="datetime-local" label={t('parties.startDate')} value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="datetime-local" label={t('parties.endDate')} value={draft.end_date} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            <TextField select size="small" label={t('field.user')} value={draft.user_id} onChange={(e) => setDraft({ ...draft, user_id: e.target.value })} sx={{ minWidth: 180 }}>
              <MenuItem value="">{t('common.all')}</MenuItem>
              {users.map((option) => (
                <MenuItem key={String(option.value)} value={String(option.value)}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {kind === 'accounts' ? (
              <>
                <TextField select size="small" label={t('field.customer')} value={draft.customer_id} onChange={(e) => setDraft({ ...draft, customer_id: e.target.value })} sx={{ minWidth: 180 }}>
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  {customers.map((option) => (
                    <MenuItem key={String(option.value)} value={String(option.value)}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" label={t('field.supplier')} value={draft.supplier_id} onChange={(e) => setDraft({ ...draft, supplier_id: e.target.value })} sx={{ minWidth: 180 }}>
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  {suppliers.map((option) => (
                    <MenuItem key={String(option.value)} value={String(option.value)}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            ) : null}
            <Button
              variant="contained"
              onClick={() => {
                setPage(0);
                setFilters({ ...draft });
              }}
            >
              {t('parties.apply')}
            </Button>
            <Button
              onClick={() => {
                setPage(0);
                setDraft(emptyFilters);
                setFilters(emptyFilters);
              }}
            >
              {t('action.clearFilters')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {[
          { label: t('parties.startBalance'), value: summary?.start },
          { label: t('parties.closeBalance'), value: summary?.close }
        ].map((box) => (
          <Card key={box.label} variant="outlined">
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatAmount(box.value ?? null)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {box.label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <TableContainer component={Card} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{sortLabel('created_at', t('field.created'))}</TableCell>
              <TableCell>{kind === 'accounts' ? t('field.account') : kind === 'customers' ? t('field.customer') : t('field.supplier')}</TableCell>
              <TableCell>{t('field.reference')}</TableCell>
              <TableCell align="right">{sortLabel('debit', t('parties.debit'))}</TableCell>
              <TableCell align="right">{sortLabel('credit', t('parties.credit'))}</TableCell>
              <TableCell align="center">{t('field.type')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <LoadingBlock />
                </TableCell>
              </TableRow>
            ) : rows.length ? (
              rows.map((row) => {
                const debit = centsToMajor(row.debit) ?? 0;
                const credit = centsToMajor(row.credit) ?? 0;
                const link = subjectLink(row);
                const morphed = row.journal?.morphed ?? null;
                return (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={link ? () => navigate(link) : undefined}
                    sx={{
                      cursor: link ? 'pointer' : undefined,
                      bgcolor: debit > 0 ? UI.warningTint : credit > 0 ? UI.successTint : undefined
                    }}
                  >
                    <TableCell>{row.created_at ? formatDateAuto(row.created_at) : '-'}</TableCell>
                    <TableCell>{morphed ? `${String(morphed.name ?? '')}${morphed.company ? ` (${String(morphed.company)})` : ''}` : '-'}</TableCell>
                    <TableCell>{String(row.subject?.reference ?? '-')}</TableCell>
                    <TableCell align="right">{debit ? formatAmount(debit) : ''}</TableCell>
                    <TableCell align="right">{credit ? formatAmount(credit) : ''}</TableCell>
                    <TableCell align="center">{typeChips(row.type)}</TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {t('parties.noData')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                {t('parties.pageTotals')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatAmount(totals.debit)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatAmount(totals.credit)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_e, next) => setPage(next)}
          onRowsPerPageChange={(e) => {
            setLimit(Number(e.target.value));
            setPage(0);
          }}
          labelRowsPerPage={t('parties.rowsPerPage')}
        />
      </TableContainer>
    </Stack>
  );
}
